// Weather service for fetching current conditions and METAR data

import config from '@/config';

const OPENWEATHER_API_KEY = config.services.openWeatherApiKey;
const AVWX_API_KEY = config.services.avwxApiKey;

export interface WeatherData {
  temperature: number;
  feelsLike: number;
  description: string;
  windSpeed: number;
  windDirection: number;
  humidity: number;
  pressure: number;
  visibility: number;
  clouds: number;
  icon: string;
}

export interface MetarData {
  station: string;
  distance: number; // km from location
  rawText: string;
  flightRules: string; // VFR, MVFR, IFR, LIFR
  time: Date;
  temperature: number;
  dewpoint: number;
  windSpeed: number;
  windGust?: number;
  windDirection: number;
  visibility: number;
  altimeter: number;
  clouds: Array<{
    type: string;
    altitude: number;
  }>;
  remarks?: string;
}

class WeatherService {
  // Get current weather from OpenWeatherMap
  async getCurrentWeather(lat: number, lon: number): Promise<WeatherData | null> {
    // No key configured (or unreplaced build placeholder) — skip quietly, METAR still works
    if (!OPENWEATHER_API_KEY || OPENWEATHER_API_KEY.startsWith('__')) {
      return null;
    }
    try {
      const response = await fetch(
        `${config.services.openWeatherApiUrl}/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}&units=metric`,
        {
          signal: AbortSignal.timeout(config.timeouts.weather)
        }
      );
      
      if (!response.ok) {
        console.error('Weather API error:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      return {
        temperature: Math.round(data.main.temp),
        feelsLike: Math.round(data.main.feels_like),
        description: data.weather[0].description,
        windSpeed: Math.round(data.wind.speed * 3.6), // Convert m/s to km/h
        windDirection: data.wind.deg,
        humidity: data.main.humidity,
        pressure: data.main.pressure,
        visibility: data.visibility / 1000, // Convert to km
        clouds: data.clouds.all,
        icon: data.weather[0].icon
      };
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      return null;
    }
  }

  // Get nearest METAR from AVWX API
  async getNearestMetar(lat: number, lon: number): Promise<MetarData | null> {
    try {
      // First, find nearest airport
      const stationResponse = await fetch(
        `${config.services.avwxApiUrl}/station/near/${lat},${lon}?n=1&airport=true&reporting=true&format=json`,
        {
          headers: {
            'Authorization': `Bearer ${AVWX_API_KEY}`
          },
          signal: AbortSignal.timeout(config.timeouts.weather)
        }
      );
      
      if (!stationResponse.ok) {
        console.error('Station API error:', stationResponse.status);
        return null;
      }
      
      const stations = await stationResponse.json();
      if (!stations || stations.length === 0) {
        return null;
      }
      
      const nearestStation = stations[0];
      const stationCode = nearestStation.station.icao;
      
      // Now get METAR for that station
      const metarResponse = await fetch(
        `${config.services.avwxApiUrl}/metar/${stationCode}?format=json`,
        {
          headers: {
            'Authorization': `Bearer ${AVWX_API_KEY}`
          },
          signal: AbortSignal.timeout(config.timeouts.weather)
        }
      );
      
      if (!metarResponse.ok) {
        console.error('METAR API error:', metarResponse.status);
        return null;
      }
      
      const metar = await metarResponse.json();
      
      return {
        station: stationCode,
        distance: Math.round(nearestStation.distance * 1.60934), // Convert miles to km
        rawText: metar.raw,
        flightRules: metar.flight_rules,
        time: new Date(metar.time.dt),
        temperature: metar.temperature?.value || 0,
        dewpoint: metar.dewpoint?.value || 0,
        windSpeed: metar.wind_speed?.value ? Math.round(metar.wind_speed.value * 1.852) : 0, // knots to km/h
        windGust: metar.wind_gust?.value ? Math.round(metar.wind_gust.value * 1.852) : undefined,
        windDirection: metar.wind_direction?.value || 0,
        visibility: metar.visibility?.value ? metar.visibility.value * 1.60934 : 10, // miles to km
        altimeter: metar.altimeter?.value || 29.92,
        clouds: metar.clouds?.map((cloud: any) => ({
          type: cloud.type,
          altitude: cloud.altitude
        })) || [],
        remarks: metar.remarks
      };
    } catch (error) {
      console.error('Failed to fetch METAR:', error);
      return null;
    }
  }

  // Get METAR via our backend proxy to the Aviation Weather Center API
  // (free, no API key, includes Canadian stations)
  async getMetarFromNOAA(lat: number, lon: number): Promise<MetarData | null> {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const token = localStorage.getItem('teaam_token');

      const response = await fetch(
        `${API_BASE_URL}/weather/metar/${lat}/${lon}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (!response.ok) return null;

      const data = await response.json();
      if (!data.success || !data.metar) return null;

      const m = data.metar;

      // Parse visibility — API returns number (statute miles) or string like "10+"
      const visMiles = typeof m.visibility === 'string'
        ? parseFloat(m.visibility) || 10
        : (m.visibility ?? 10);

      return {
        station: m.station,
        distance: m.distance,
        rawText: m.rawText,
        flightRules: m.flightRules || 'UNK',
        time: new Date(m.time),
        temperature: m.temperature ?? 0,
        dewpoint: m.dewpoint ?? 0,
        windSpeed: (m.windSpeed ?? 0) * 1.852,     // knots → km/h
        windGust: m.windGust ? m.windGust * 1.852 : undefined,
        windDirection: m.windDirection ?? 0,
        visibility: visMiles * 1.60934,              // statute miles → km
        // AWC returns hPa; UI displays inHg (values > 100 are clearly hPa)
        altimeter: m.altimeter ? (m.altimeter > 100 ? m.altimeter * 0.02953 : m.altimeter) : 29.92,
        clouds: m.clouds || [],
        remarks: undefined,
      };
    } catch (error) {
      console.error('Failed to fetch METAR:', error);
      return null;
    }
  }

  // Format METAR for display
  formatMetarForDisplay(metar: MetarData): string {
    return `
      Station: ${metar.station} (${metar.distance}km away)
      Flight Rules: ${metar.flightRules}
      Time: ${metar.time.toLocaleTimeString()}
      
      Temperature: ${metar.temperature}°C / Dewpoint: ${metar.dewpoint}°C
      Wind: ${metar.windDirection}° at ${metar.windSpeed} km/h${metar.windGust ? ` gusting ${metar.windGust} km/h` : ''}
      Visibility: ${metar.visibility.toFixed(1)} km
      Altimeter: ${metar.altimeter.toFixed(2)} inHg
      
      ${metar.rawText}
    `;
  }
}

export default new WeatherService();